/** @import '../../../../common/types' */
import * as React from 'react'
import { useRef } from 'react'
import { useModal, useModalExtra } from '../../../../_components/Modal'
import { CategoryModalContent, CertificationModalContent } from './../CertificationInfo'
import { nanoid } from 'nanoid'
import { useSelector } from 'react-redux'
import { getDynamoDB } from '../../../../common/utils/DynamoDbUtils'
import { table, viewId } from '../../../../_constants/Code'
import { errorLog } from '../../../../common/logger/Logger'
import { get } from 'lodash'

const useCertificationInfo = () => {}

// 親ノードのキー
export const rootNodeKey = 'node_0'
// ツリーの深さ上限
export const maxDepth = 3
// カテゴリーなしの表示テキスト
export const noCategoryLabel = 'カテゴリーなし'
// カテゴリーなしのキー
export const noCategoryKey = '(メインカテゴリなし)'

export const useTree = () => {
  const corporationNumber = useSelector((state) => state.company.corporationNumber)
  const userId = useSelector((state) => state.user.sub)

  /** @returns {CertificationTree} */
  const initTree = () => {
    const initNode = {
      [rootNodeKey]: createNode(undefined, rootNodeKey, [noCategoryKey]),
      [noCategoryKey]: createNode(
        {
          CertificationData: {
            CategoryName: noCategoryLabel
          },
          state: {
            editable: false
          },
          Metadata: {
            SuperNode: rootNodeKey,
            Depth: 1,
            DisplayOrder: 1
          }
        },
        noCategoryKey
      )
    }

    initNode[rootNodeKey].node = rootNodeKey
    return initNode
  }

  /**
   * @function createNode
   * @param {CertificationNodeData} _data
   * @param {string} id ノードID
   * @param {string[]} SubNode
   * @returns {CertificationNode}
   */
  const createNode = (_data = {}, id, SubNode = []) => ({
    CorporationNumber: corporationNumber,
    CertificationData: {},
    CertificationCode: '-',
    node: id,
    state: {
      deleted: false,
      fold: false,
      editable: true
    },
    Metadata: {
      SuperNode: null,
      Depth: null,
      DisplayOrder: 0
    },
    SubNode,
    CreateUserId: userId || '',
    CreateDateTime: null,
    UpdateUserId: '',
    UpdateDateTime: null,
    ..._data
  })

  /**
   * IDを作るための関数
   *
   * @param {CertificationNodeData} nodeData
   * @param {number} depth
   * @returns
   */
  const createId = (nodeData, depth) => {
    const name = nodeData?.CategoryName || nodeData?.Name || ''
    const randomString = Math.random().toString(20).substring(2, 7)

    if (!name) return nanoid()

    return `${depth}_${name}_${randomString}`
  }

  /**
   * ツリーにノードを追加関数
   *
   * @param {CertificationTree} tree
   * @returns {function(string, CertificationNodeData, string[],number):string} // nodeId 親ノードID、ノードデータ, 子ID、深さ
   */
  const addNodeToTree = (tree) => (parentNodeId, nodeData, children, depth) => {
    // node_0 is root
    const node = createNode({ CertificationData: nodeData })
    const nodeId = createId(nodeData, depth)

    const isRootNode = nodeId === rootNodeKey

    node.node = nodeId
    node.Metadata.SuperNode = isRootNode ? null : parentNodeId
    node.SubNode = children || []
    node.Metadata.Depth = depth || getNodeDepth(tree)(node) || 3
    node.Metadata.DisplayOrder = null

    tree[nodeId] = node

    const parentNode = tree[parentNodeId]
    parentNode.state.fold = false

    if (parentNode?.Metadata?.Depth === 1 && node.Metadata.Depth === 3) {
      parentNode?.SubNode.unshift(nodeId)
    } else {
      parentNode?.SubNode.push(nodeId)
    }

    parentNode?.SubNode?.forEach((e, index) => {
      if (get(tree, `${e}.Metadata.DisplayOrder`) === undefined) {
        return
      }
      tree[e].Metadata.DisplayOrder = index + 1
    })

    if (parentNodeId === rootNodeKey) {
      tree[noCategoryKey].Metadata.DisplayOrder = node.Metadata.DisplayOrder + 1
    }

    return nodeId
  }

  /**
   * @function getCategories ツリーのカテゴリを算出
   * @param {CertificationTree} tree
   */
  const getCategories = (tree, isMainCategory) => {
    const predicate = ({ node, Metadata, CertificationData }) => {
      const filterMain = isMainCategory ? 3 : 2
      return node !== rootNodeKey && Metadata?.Depth < filterMain && CertificationData.CategoryName !== noCategoryLabel
    }

    return Object.values(tree).filter(predicate)
  }

  /** @function ノードを見る */
  const traverse = (tree) => (node, callback) => {
    if (node.SubNode.length > 0) {
      const queue = [...node.SubNode]

      while (queue.length > 0) {
        const nodeId = queue.shift()
        callback(nodeId)
        if (tree[nodeId] && tree[nodeId].SubNode.length > 0) queue.push(...tree[nodeId].SubNode)
      }
    }
  }

  /**
   * @function ツリーから ノードを削除
   * @param {CertificationTree} tree
   * @returns {function(string):CertificationTree}
   */
  const removeNode = (tree) => (nodeId) => {
    const currentNode = tree[nodeId]
    const parentNode = tree[currentNode.Metadata.SuperNode]

    if (currentNode.CreateDateTime !== null) {
      currentNode.state.deleted = !currentNode.state.deleted
      currentNode.state.fold = true

      traverse(tree)(currentNode, (nodeId) => {
        if (!tree?.[nodeId]) return
        tree[nodeId].state.deleted = currentNode.state.deleted
      })
    } else {
      delete tree[nodeId]
      if (parentNode) parentNode.SubNode = parentNode.SubNode.filter((x) => x !== nodeId)
    }

    return tree
  }

  /**
   * @function ノードを更新
   * @param {CertificationTree} tree
   * @returns {function(Node):CertificationTree}
   */
  const updateNode = (tree) => (node) => {
    const currentNode = tree[node.node]
    if (!currentNode) return tree

    currentNode.CertificationData = { ...node.CertificationData }
    currentNode.CertificationCode = node.CertificationCode
    currentNode.SubNode = [...node.SubNode]
    currentNode.UpdateUserId = userId

    return tree
  }

  /**
   * @function ノードの深さを算出
   * @param {CertificationTree} tree
   * @returns {function(CertificationNode):number}
   */
  const getNodeDepth = (tree) => (node) => {
    let counter = 1
    let currentParent = tree[node.Metadata.SuperNode]

    if (!currentParent) return counter - 1

    while (currentParent.node !== rootNodeKey && counter < Object.keys(tree).length) {
      currentParent = tree[currentParent.Metadata.SuperNode]
      counter++
    }

    return counter
  }

  return {
    initTree,
    createNode,
    addNodeToTree,
    removeNode,
    updateNode,
    getNodeDepth,
    getCategories
  }
}

/**
 * @function useCategory
 * @param {ReturnType<typeof React.useState<CertificationTree>>} mainState
 */
export const useCategory = (mainState) => {
  const [certificationMasterTree, setCertificationMasterTree] = mainState

  const { updateNode, addNodeToTree, getCategories } = useTree()

  const { _closeUpModal } = useModalExtra()

  const categoryFormRef = useRef()
  const showModal = useModal()

  /**
   * @function _addCategory カテゴリを追加
   * @param {Map} data
   */
  const _addCategory = (data) => {
    const tempState = { ...certificationMasterTree }

    const parent = data.SuperNode
    const root = parent === null ? rootNodeKey : parent

    addNodeToTree(tempState)(
      root,
      {
        CategoryName: String(data.CategoryName).trim()
      },
      [],
      data.Depth
    )

    setCertificationMasterTree(tempState)
  }

  const handleFormSubmit = (formData, _, resetForm) => {
    _addCategory(formData)
    _closeUpModal()
  }

  const handleCloseModal = (resetForm) => {
    _closeUpModal()
  }

  /**
   * @function openCategoryModal カテゴリー 追加モダルを表示
   * @param {CertificationNode} parentNode
   */
  const openCategoryModal = (parentNode) => {
    const isMainCategory = parentNode.node === rootNodeKey

    const prefix = isMainCategory ? 'メイン' : 'サブ'
    const title = prefix.concat('カテゴリー設定')

    const categories = getCategories(certificationMasterTree, isMainCategory)

    showModal({
      isWide: false,
      isCancel: false,
      title,
      body: (
        <CategoryModalContent
          key={Date.now()}
          isMainCategory={isMainCategory}
          categoryList={categories}
          ref={categoryFormRef}
          parentNode={parentNode}
          onClose={handleCloseModal}
          onSubmit={handleFormSubmit}
        />
      )
    })
  }

  const openUpdateCategoryModal = (node) => {
    if (!node) return

    const parentNode = certificationMasterTree[node.Metadata.SuperNode]
    const isMainCategory = parentNode.node === rootNodeKey

    /**
     * @function ノードのデータの更新
     * @param {Map} formData フォームのデータ
     */
    const _onUpdateNode = (formData) => {
      const nodeId = node.node
      const currentNode = structuredClone(certificationMasterTree[nodeId])
      if (!currentNode) return

      currentNode.CertificationData.CategoryName = String(formData.CategoryName)?.trim()
      currentNode.Metadata.SuperNode = formData.SuperNode

      setCertificationMasterTree((s) => updateNode({ ...s })(currentNode))
      handleCloseModal()
    }

    const key = Date.now()

    showModal({
      isCancel: false,
      isWide: false,
      title: (isMainCategory ? 'メイン' : 'サブ') + 'カテゴリー設定',
      body: (
        <CategoryModalContent
          editMode
          isMainCategory={isMainCategory}
          key={key}
          ref={categoryFormRef}
          parentNode={parentNode}
          categoryList={[]}
          defaultData={{
            CategoryName: node.CertificationData.CategoryName,
            SuperNode: node.Metadata.SuperNode
          }}
          onSubmit={_onUpdateNode}
          onClose={handleCloseModal}
        />
      )
    })
  }

  return { openUpdateCategoryModal, openCategoryModal }
}

/**
 * @function useCertificationData
 * @param {ReturnType<typeof React.useState<CertificationTree>>} mainState
 */
export const useCertificationData = (mainState) => {
  const showModal = useModal()
  const { _closeUpModal } = useModalExtra()
  const certificationDataRef = useRef()

  const [tree, setCertificationMasterTree] = mainState

  const { addNodeToTree, updateNode } = useTree()

  const handleAddCertification = (parentNode, formData) => {
    const payload = { ...formData }

    for (const k in payload) {
      payload[k] = typeof payload[k] === 'string' ? payload[k].trim() : payload[k]
    }

    setCertificationMasterTree((s) => {
      const temp = { ...s }
      const id = addNodeToTree(temp)(parentNode.node, payload, [], 3)

      temp[id].CertificationCode = formData.CertificationCode
      return temp
    })
  }

  const onClose = (resetForm) => {
    _closeUpModal()
    resetForm()
  }

  /**
   * @function 資格モダルの表示
   * @param {Node} parentNode 親ノード
   */
  const openCertificationModal = (parentNode) => {
    showModal({
      title: '資格情報設定',
      body: (
        <CertificationModalContent
          key={Date.now()}
          tree={tree}
          ref={certificationDataRef}
          onClose={onClose}
          onSubmit={(data, resetFormData) => {
            handleAddCertification(parentNode, data)
            resetFormData()
            _closeUpModal()
          }}
        />
      ),
      isFooter: false,
      isCancel: false,
      isWide: false
    })
  }

  /**
   * @function 資格更新モダルの表示
   * @param {CertificationNode} currentNode 現在のノード
   */
  const openUpdateCertificationModal = (currentNode) => {
    const currentNodeData = { ...currentNode?.CertificationData, CertificationCode: currentNode?.CertificationCode }

    if (!currentNodeData) return

    const handleFormSubmit = (data) => {
      for (const k in data) {
        data[k] = typeof data[k] === 'string' ? data[k].trim() : data[k]
      }
      currentNode.CertificationData = { ...currentNode.CertificationData, ...data }
      currentNode.CertificationCode = data.CertificationCode

      setCertificationMasterTree((s) => updateNode({ ...s })(currentNode))
    }

    showModal({
      isFooter: false,
      isCancel: false,
      isWide: false,
      title: '資格情報設定',
      body: (
        <CertificationModalContent
          editMode
          key={currentNode.node}
          node={currentNode.node}
          tree={tree}
          defaultData={currentNodeData}
          ref={certificationDataRef}
          onSubmit={(data) => {
            handleFormSubmit(data)
            _closeUpModal()
          }}
          onClose={_closeUpModal}
        />
      )
    })
  }

  return {
    openUpdateCertificationModal,
    openCertificationModal
  }
}

export const useCertificationResources = () => {
  const corporationNumber = useSelector((state) => state.company.corporationNumber)
  const userId = useSelector((state) => state.user.sub)

  /**
   * @function マスターを取得
   * @typedef {Object} CertificationReturnValue
   * @property {CertificationTree} certifications //資格ツリー
   * @property {Record<string, string>} certificationCodeMap // 資格コードとIDのマップ
   * @returns {Promise<CertificationReturnValue>}
   */
  const getCertifications = async () => {
    try {
      const certifications = await getDynamoDB(table.Certification, null, corporationNumber)
      if (!certifications) throw new Error('データ取得に失敗しました ')

      let mainMax = 0
      const entries = certifications.map((e) => {
        const foldState = e.node !== rootNodeKey && e.Metadata.Depth < 3

        if (e.Metadata.Depth === 1) {
          mainMax = Math.max(mainMax, e.Metadata.DisplayOrder)
        }

        return [e.node, { ...e, state: { fold: foldState, editable: true } }]
      })

      // カテゴリーなしを最後に移動させる処理
      const _certifications = Object.fromEntries(entries)

      if (_certifications[noCategoryKey]) {
        const noCategoryDisplay = _certifications?.[noCategoryKey]?.Metadata?.DisplayOrder || 1
        _certifications[noCategoryKey].CertificationData.CategoryName = noCategoryLabel

        if (noCategoryDisplay !== mainMax) {
          _certifications[noCategoryKey].Metadata.DisplayOrder = mainMax + 1
        }
      }

      const certificationCodeMap = Object.fromEntries(certifications.map((e) => [e.CertificationCode, e.node]))

      return { certifications: _certifications, certificationCodeMap }
    } catch (error) {
      console.log(error)
      errorLog('E00001', userId, viewId.certificationInfo, 'getMaster', error)
    }
  }

  /** 適用対象を取得 */
  const getMaster = async () => {
    try {
      const master = await getDynamoDB(table.Master, null, corporationNumber, {
        CorporationNumber: corporationNumber,
        MasterId: viewId.certificationInfo
      })

      return master[0]
    } catch (error) {
      console.error(error)
      errorLog('E000001', userId, viewId.certificationInfo, 'getMaster', error)
    }
  }
  /**
   * 資格が利用されているかを確認
   *
   * @param {string[]} certificationCodeList
   * @returns {Promise<boolean>}
   */
  async function checkIfCertificationIsInUse(certificationCodeList) {
    try {
      // get kpi
      // get certification set

      const pKpiList = getDynamoDB(table.KpiTargetSubInfo, null, corporationNumber)
      const pCertificationSetList = getDynamoDB(table.CertificationSet, null, corporationNumber)

      const [kpiList, certificationSetList] = await Promise.all([pKpiList, pCertificationSetList])

      const kpiCheck = kpiList.some((e) => e.TargetValue.some((x) => certificationCodeList.includes(x.Code)))

      const certificationSetCheck = certificationSetList
        .map((e) => e.CertificationCodeList)
        .flat()
        .some((e) => certificationCodeList.includes(e))

      return kpiCheck || certificationSetCheck
    } catch (error) {
      console.log(error)
      errorLog('E000001', userId, viewId.certificationInfo, 'getMaster', error)
      return true
    }
  }

  return { getCertifications, getMaster, checkIfCertificationIsInUse }
}

export default useCertificationInfo
